import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/theme';
import {
  BrandMark,
  IconAjustes,
  IconAssinantes,
  IconBell,
  IconCampanhas,
  IconDinheiro,
  IconExit,
  IconInicio,
  IconLoja,
  IconProdutos,
  IconSearch,
  IconSun,
  IconVendas,
} from './Icons';
import s from './shell.module.css';

const NAV = [
  { to: '/', label: 'Início', Icon: IconInicio, end: true },
  { to: '/produtos', label: 'Produtos', Icon: IconProdutos, end: false },
  { to: '/loja', label: 'Loja', Icon: IconLoja, end: false },
  { to: '/vendas', label: 'Vendas', Icon: IconVendas, end: false },
  { to: '/assinantes', label: 'Assinantes', Icon: IconAssinantes, end: false },
  { to: '/dinheiro', label: 'Dinheiro', Icon: IconDinheiro, end: false },
  { to: '/campanhas', label: 'Campanhas', Icon: IconCampanhas, end: false },
  { to: '/ajustes', label: 'Ajustes', Icon: IconAjustes, end: false },
] as const;

/** Shell fixo (rail + topbar) — porta do P5 (ADR 0019 §3). Todas as rotas do painel entram
 *  aqui dentro via `<Outlet/>`; a página só cuida do `.content`. */
export function Shell() {
  const { claims, logout } = useAuth();
  const { toggle } = useTheme();
  const { pathname } = useLocation();
  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const person = claims?.name ?? claims?.email ?? 'Parceiro';
  const initial = person.charAt(0).toUpperCase();
  const crumb = NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))?.label ?? 'Storeo';

  return (
    <div className={s.app}>
      <aside className={s.rail}>
        <div className={s.brand}>
          <span className={s.brandDot} aria-hidden="true">
            <BrandMark />
          </span>
          <b>Storeo</b>
        </div>
        <nav className={s.nav} aria-label="Principal">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `${s.navLink} ${isActive ? s.navLinkActive : ''}`}
            >
              <Icon />
              <span className={s.navLabel}>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className={s.spacer} />
        <div className={s.me}>
          <span className={s.meAvatar} aria-hidden="true">
            {initial}
          </span>
          <span className={s.meWho}>
            <b>{person}</b>
            <span>{store.data?.name ?? '—'}</span>
          </span>
          <button className={s.meExit} onClick={logout} aria-label="Sair" title="Sair">
            <IconExit />
          </button>
        </div>
      </aside>

      <div className={s.main}>
        <header className={s.topbar}>
          <span className={s.crumb}>
            <b>{crumb}</b>
          </span>
          <span className={s.grow} />
          <span className={s.search}>
            <IconSearch />
            Buscar
            <span className={s.searchKey}>⌘K</span>
          </span>
          <button className={s.iconbtn} onClick={toggle} aria-label="Alternar tema" title="Alternar tema">
            <IconSun />
          </button>
          <button className={s.iconbtn} aria-label="Notificações">
            <IconBell />
          </button>
        </header>

        <main className={s.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
